<template>
  <v-dialog
      :value="value"
      @input="$emit('input', $event)"
      max-width="1100px"
      scrollable
  >
    <v-card v-if="planCopy">
      <v-card-title>
        Meal Box Plan — {{ planCopy.name }} ({{ planCopy.id }})
        <v-spacer/>
        <v-chip v-if="changed" color="warning" small class="mr-2">unsaved changes</v-chip>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pb-0">
        <v-text-field
            v-model="planCopy.name"
            :disabled="readOnly"
            label="Plan name"
            style="max-width: 30em"
            data-cy="box-plan-name"
        />
      </v-card-text>
      <v-tabs v-model="tab" class="px-4">
        <v-tab>Sizes &amp; pricing</v-tab>
        <v-tab>Streams &amp; filters</v-tab>
        <v-tab>Auto-populate</v-tab>
        <v-tab>Swap picker</v-tab>
        <v-tab>Preferences</v-tab>
      </v-tabs>
      <v-card-text class="box-plan-tabs">
      <v-tabs-items v-model="tab">
      <v-tab-item>

      <h3>Box sizes (per-meal price by box size)</h3>
      <v-simple-table dense style="max-width: 45em" data-cy="box-sizes">
        <thead>
        <tr>
          <th>size (meals)</th>
          <th>price per meal</th>
          <th>free shipping</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(row, i) of planCopy.box_sizes" v-bind:key="`size-${i}`">
          <td>{{ row.size }}</td>
          <td>{{ formatCurrency(row.price) }} <span class="text--secondary">({{ row.price }}¢)</span></td>
          <td>{{ row.free_shipping ? 'yes' : '—' }}</td>
          <td class="text-right">
            <v-btn small text :disabled="readOnly" @click="openSizeDialog(i)">Edit</v-btn>
          </td>
        </tr>
        <tr v-if="!planCopy.box_sizes.length">
          <td colspan="4" class="text--secondary">no sizes — the plan sells no box</td>
        </tr>
        </tbody>
      </v-simple-table>
      <v-btn small outlined class="mt-2" :disabled="readOnly" @click="openSizeDialog(null)">Add Size</v-btn>

      <h3 class="mt-6">Maximum box size</h3>
      <v-text-field
          type="number"
          style="max-width: 30em"
          :disabled="readOnly"
          v-model="planCopy.box_max_size"
          label="box_max_size (meals per delivery)"
          hint="The biggest box a customer can order on one delivery (corporate orders). Sizes above the
            largest tier up to this are priced like the largest tier. Empty = the largest tier is the maximum."
          persistent-hint
      />

      <h3 class="mt-6">Chef's choice base price</h3>
      <v-text-field
          type="number"
          style="max-width: 30em"
          :disabled="readOnly"
          v-model="planCopy.box_base_price"
          label="box_base_price (cents)"
          :hint="`${formatCurrency(planCopy.box_base_price)} — CC meals above this add the difference to the
            box rate; at or below it they charge the plain rate (never a discount)`"
          persistent-hint
      />

      <h3 class="mt-6">Breakfast fruit cup</h3>
      <v-checkbox
          :disabled="readOnly"
          v-model="planCopy.breakfast_fruitcup"
          label="Include the free fruit cup with box breakfasts"
          hint="the regular streams always add it; the box only when this is on (kitchen packing option)"
          persistent-hint
      />

      </v-tab-item>
      <v-tab-item>
      <h3>Streams</h3>
      <p class="text--secondary">
        How each stream feeds the box under the current rules (edit via the
        surcharges and filters below). A stream only contributes meals when it
        has a weekly menu; a surcharged meal charges the plain rate when a
        plain-priced stream also offers it.
      </p>
      <v-simple-table dense style="max-width: 45em">
        <thead>
        <tr>
          <th>stream</th>
          <th>in the box</th>
          <th>surcharge</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="s of streamSummary" v-bind:key="s.id">
          <td>{{ s.id }}<span v-if="s.hidden" class="text--secondary"> (hidden)</span></td>
          <td>
            <v-chip small :color="s.excluded ? undefined : 'success'">
              {{ s.excluded ? 'excluded' : 'included' }}
            </v-chip>
          </td>
          <td>
            <v-chip v-for="(label, i) of s.surcharges" v-bind:key="i" small color="warning" class="mr-1">
              {{ label }}
            </v-chip>
            <span v-if="!s.excluded && !s.surcharges.length" class="text--secondary">plain box rate</span>
          </td>
        </tr>
        </tbody>
      </v-simple-table>

      <h3 class="mt-6">Stream surcharges</h3>
      <p class="text--secondary">
        Adds to the box rate when a meal is ONLY offered by the listed streams
        (a meal also on a plain-priced stream charges the plain rate).
        Chef's choice items never take a stream surcharge.
      </p>
      <v-row v-for="(rule, i) of planCopy.box_surcharges" v-bind:key="`surcharge-${i}`" align="center">
        <v-col cols="4">
          <v-select multiple chips :disabled="readOnly" :items="streamIds" v-model="rule.streams" label="streams"/>
        </v-col>
        <v-col cols="4">
          <v-combobox multiple chips :disabled="readOnly" :items="knownTods" v-model="rule.tods"
                      label="tods (empty = all)"/>
        </v-col>
        <v-col cols="3">
          <v-text-field type="number" :disabled="readOnly" v-model="rule.amount"
                        label="amount (cents)" :hint="`+${formatCurrency(rule.amount)}`" persistent-hint/>
        </v-col>
        <v-col cols="1">
          <v-btn icon color="red" :disabled="readOnly" @click="planCopy.box_surcharges.splice(i, 1)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-btn small outlined :disabled="readOnly" @click="addSurcharge">Add Surcharge</v-btn>

      <h3 class="mt-6">Included meals (box_filters)</h3>
      <div class="mb-1">
        <span class="mr-2">In the box:</span>
        <v-chip v-for="t of todSummary.included" v-bind:key="`tod-in-${t}`"
                small color="success" class="mr-1 mb-1">{{ t }}</v-chip>
        <span v-if="!todSummary.included.length" class="text--secondary">nothing — no meal type passes the filters</span>
      </div>
      <div class="mb-1">
        <span class="mr-2">Kept out:</span>
        <v-chip v-for="t of todSummary.excluded" v-bind:key="`tod-out-${t}`"
                small outlined class="mr-1 mb-1">{{ t }}</v-chip>
        <span v-if="!todSummary.excluded.length" class="text--secondary">nothing — every meal type passes</span>
      </div>
      <p v-if="priceWindow" class="text--secondary mb-1">
        Plus a meal must be priced {{ priceWindow }} to be offered in the box.
      </p>
      <p class="text--secondary">
        The rules behind the summary: include rules are an allowlist (only listed values
        pass — new chef's choice categories stay out of the box until added); exclude
        rules drop matches; min/max bound a numeric attribute. Matching is
        case-insensitive. Note: lunch/dinner appear in the box as <code>entree</code>,
        CC categories as their lowercased name.
      </p>
      <v-row v-for="(f, i) of planCopy.box_filters" v-bind:key="`filter-${i}`" align="center">
        <v-col cols="2">
          <v-combobox :disabled="readOnly" :items="knownAttributes" v-model="f.attribute" label="attribute"/>
        </v-col>
        <v-col cols="2">
          <v-select :disabled="readOnly" :items="['include', 'exclude', 'range']"
                    :value="filterType(f)" @change="setFilterType(f, $event)" label="rule"/>
        </v-col>
        <v-col cols="7">
          <v-combobox v-if="f.include" multiple chips :disabled="readOnly"
                      :items="valuesFor(f.attribute)" v-model="f.include" label="included values"/>
          <v-combobox v-else-if="f.exclude" multiple chips :disabled="readOnly"
                      :items="valuesFor(f.attribute)" v-model="f.exclude" label="excluded values"/>
          <v-row v-else>
            <v-col>
              <v-text-field type="number" :disabled="readOnly" v-model="f.min" label="min"/>
            </v-col>
            <v-col>
              <v-text-field type="number" :disabled="readOnly" v-model="f.max" label="max (empty = none)"/>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="1">
          <v-btn icon color="red" :disabled="readOnly" @click="planCopy.box_filters.splice(i, 1)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-btn small outlined :disabled="readOnly" @click="addFilter">Add Filter</v-btn>

      </v-tab-item>
      <v-tab-item>
      <h3>Auto-populate — Chef's Choice categories</h3>
      <p class="text--secondary">
        What the AUTO-POPULATE may pick, in the admin-defined menu categories
        (Menu Categories editor — matched by NAME, case-insensitive; only
        chef's choice meals carry these). Excluded categories stay available
        through the swap picker; an "only" list restricts auto-picks to those
        categories. Breakfasts and surcharged meals are never auto-picked
        regardless.
      </p>
      <v-row>
        <v-col cols="6">
          <v-combobox
              multiple chips deletable-chips clearable
              :disabled="readOnly"
              :items="menuCategoryNames"
              :loading="menuCategoriesLoading"
              v-model="planCopy.box_autopick_menu_categories.exclude"
              label="never auto-populate these categories"
          />
        </v-col>
        <v-col cols="6">
          <v-combobox
              multiple chips deletable-chips clearable
              :disabled="readOnly"
              :items="menuCategoryNames"
              :loading="menuCategoriesLoading"
              v-model="planCopy.box_autopick_menu_categories.include"
              label="only auto-populate these categories (empty = all)"
          />
        </v-col>
      </v-row>

      <h3 class="mt-6">Auto-populate — Chef's Choice ranking</h3>
      <p class="text--secondary">
        How auto-populate orders chef's choice meals against the stream meals.
        Empty = off (the customer's rating alone decides). Whatever the
        settings, a meal auto-populate may not pick (the categories above,
        surcharged, breakfast) is never picked, and no meal is suggested for
        two deliveries in a row.
      </p>
      <v-row>
        <v-col cols="6">
          <v-text-field
              type="number" min="0"
              :disabled="readOnly"
              v-model="planCopy.box_cc_lead_limit"
              label="box_cc_lead_limit (CC meals ahead of stream meals)"
              hint="At most this many chef's choice meals may rank ahead of a stream meal; the rest come after
                every stream meal. 0 = stream meals always first."
              persistent-hint
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
              type="number" min="0"
              :disabled="readOnly"
              v-model="planCopy.box_cc_per_delivery"
              label="box_cc_per_delivery (CC meals per delivery)"
              hint="A delivery's box takes at most this many chef's choice meals (the best ranked, counting ones the
                customer chose); the others are left out of that delivery."
              persistent-hint
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
              type="number" min="0"
              :disabled="readOnly"
              v-model="planCopy.box_cc_rotation"
              label="box_cc_rotation (points)"
              hint="Varies the chef's choice order by delivery date, up to this many rating points: the same for
                every customer on a date, different on the next. A meal's rating is typically -10 to +20."
              persistent-hint
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
              type="number" min="0"
              :disabled="readOnly"
              v-model="planCopy.box_cc_featured_bonus"
              label="box_cc_featured_bonus (points)"
              hint="Added to chef's choice meals in the Featured menu category, unless the customer hates an
                ingredient, misses a nutrient goal or is allergic to it."
              persistent-hint
          />
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col cols="6">
          <v-text-field
              v-model="preview.customer"
              label="preview for customer (uid or email, optional)"
              hint="The boxes auto-populate would fill from the next ordering week with the settings above as they
                are now, saved or not. Empty: no customer's preferences, allergies or past boxes — average stars."
              persistent-hint
              data-cy="box-ranking-preview-customer"
              @keyup.enter="openPreview"
          />
        </v-col>
        <v-col cols="2">
          <v-select v-model="preview.weeks" :items="[1, 2, 3, 4]" label="weeks"/>
        </v-col>
        <v-col cols="2">
          <v-btn outlined color="primary" :loading="preview.loading"
                 data-cy="box-ranking-preview" @click="openPreview">
            Preview
          </v-btn>
        </v-col>
      </v-row>

      </v-tab-item>
      <v-tab-item>
      <h3>Swap picker — Chef's Choice categories</h3>
      <p class="text--secondary">
        What the SWAP PICKER offers from chef's choice, in the same menu
        categories. Excluded categories can't be swapped into the box at all
        (and are never auto-picked either); a meal already in a box still
        shows on its card.
      </p>
      <v-row>
        <v-col cols="6">
          <v-combobox
              multiple chips deletable-chips clearable
              :disabled="readOnly"
              :items="menuCategoryNames"
              :loading="menuCategoriesLoading"
              v-model="planCopy.box_swap_menu_categories.exclude"
              label="never offer these categories in the picker"
          />
        </v-col>
        <v-col cols="6">
          <v-combobox
              multiple chips deletable-chips clearable
              :disabled="readOnly"
              :items="menuCategoryNames"
              :loading="menuCategoriesLoading"
              v-model="planCopy.box_swap_menu_categories.include"
              label="only offer these categories (empty = all)"
          />
        </v-col>
      </v-row>

      </v-tab-item>
      <v-tab-item>
      <PlanHiddenPreferences
          :disabled="readOnly"
          v-model="planCopy.hidden_preferences"
      />
      </v-tab-item>
      </v-tabs-items>
      </v-card-text>
      <v-card-actions>
        <v-btn outlined @click="resetCopy" :disabled="readOnly || saving">Cancel Changes</v-btn>
        <v-spacer/>
        <v-btn outlined color="primary" @click="save" :disabled="readOnly || saving" :loading="saving">Save</v-btn>
      </v-card-actions>
      <v-snackbar v-model="snackbar" :timeout="4000">{{ snackbarText }}</v-snackbar>
      <v-dialog v-model="sizeDialog.open" max-width="420px">
        <v-card data-cy="box-size-dialog">
          <v-card-title>{{ sizeDialog.index === null ? 'Add a box size' : `Box of ${sizeDialog.original}` }}</v-card-title>
          <v-card-text>
            <v-text-field type="number" min="1" v-model="sizeDialog.size" label="size (meals per box)" autofocus/>
            <v-text-field type="number" min="1" v-model="sizeDialog.price" label="price per meal (cents)"
                          :hint="formatCurrency(sizeDialog.price)" persistent-hint/>
            <v-checkbox v-model="sizeDialog.free_shipping" label="free shipping at this size"/>
            <p v-if="sizeDialog.error" class="error--text mb-0">{{ sizeDialog.error }}</p>
          </v-card-text>
          <v-card-actions>
            <v-btn v-if="sizeDialog.index !== null" text color="red" @click="deleteSize">Delete</v-btn>
            <v-spacer/>
            <v-btn text @click="sizeDialog.open = false">Cancel</v-btn>
            <v-btn outlined color="primary" @click="applySize">{{ sizeDialog.index === null ? 'Add' : 'Apply' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="preview.open" fullscreen>
        <v-card class="box-ranking-preview">
          <v-toolbar dense flat>
            <v-toolbar-title>Box ranking preview — {{ preview.shownFor || 'no customer' }}</v-toolbar-title>
            <v-spacer/>
            <v-btn text :loading="preview.loading" @click="openPreview">Refresh</v-btn>
            <v-btn icon @click="preview.open = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <!-- the api's html page, written into the frame (srcdoc): an iframe
               src could not carry the login token -->
          <iframe :srcdoc="preview.html" title="box ranking report" data-cy="box-ranking-preview-frame"/>
        </v-card>
      </v-dialog>
    </v-card>
    <v-card v-else>
      <v-card-title>Meal Box Plan</v-card-title>
      <v-card-text>No box plan found (a plan whose stream is flagged box — streams/&lt;id&gt;.box).</v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {mapActions, mapState} from "vuex";
import {deepCopy, formatCurrency} from "@/store/utils";
import diff from "microdiff";
import api from "@/api";
import PlanHiddenPreferences from "@/components/PlanHiddenPreferences.vue";

// the box catalog's tod values: lunch/dinner fold into 'entree' and CC
// categories arrive lowercased — suggestions only, a combobox accepts more
const KNOWN_TODS = [
  'entree', 'breakfast', 'salads', 'single serve entree', 'sides', 'sandwiches',
  'snack', 'extras', 'carnivore meals', 'family size', 'holiday meals', 'protein side',
];

// the preview's last customer, remembered per browser
const PREVIEW_CUSTOMER_KEY = 'box-ranking-preview-customer';
function readPreviewCustomer() {
  try {
    return localStorage.getItem(PREVIEW_CUSTOMER_KEY) || '';
  } catch (e) {
    return '';
  }
}

// the plan's chef's choice ranking settings (fed-lib box-autopopulate
// ccRulesOf) — numbers, absent = off
const BOX_CC_RULE_KEYS = ['box_cc_lead_limit', 'box_cc_per_delivery', 'box_cc_rotation', 'box_cc_featured_bonus'];

export default {
  name: "BoxPlanEditor",
  components: {PlanHiddenPreferences},
  props: {
    // v-model: whether the dialog is open
    value: {type: Boolean, default: false, required: false},
    readOnly: {type: Boolean, default: false, required: false},
    // the plan to edit (any plan on the box stream); without it, the first
    // plan on the box stream
    planId: {type: String, default: null, required: false},
  },
  data() {
    return {
      planCopy: null,
      saving: false,
      snackbar: false,
      snackbarText: '',
      knownTods: KNOWN_TODS,
      knownAttributes: ['tod', 'stream', 'base_price'],
      menuCategoryNames: [],
      menuCategoriesLoading: false,
      // the box ranking report (fed-nutrition-api v2/box-ranking-report)
      preview: {
        customer: readPreviewCustomer(),
        // the customer the shown report is for ('' = none)
        shownFor: '',
        weeks: 2,
        open: false,
        loading: false,
        html: '',
      },
      tab: 0,
      // the box size being added (index null) or edited; applied to the
      // plan copy, saved with the plan
      sizeDialog: {open: false, index: null, original: null, size: null, price: null, free_shipping: false, error: ''},
    }
  },
  mounted() {
    this.fetchMenuCategories();
    return Promise.all([this.fetchPlans(), this.fetchStreams()])
        .then(() => this.resetCopy());
  },
  watch: {
    boxPlan: 'resetCopy',
  },
  computed: {
    ...mapState(['plans', 'streams']),
    // the plan being edited: `planId`, else the first plan whose STREAM
    // orders as a box (streams/<id>.box — fed-lib ordersAsBox). Such a plan
    // must price by box_sizes; this editor is where they are set
    boxPlan() {
      const onBoxStream = (p) => !!(p && p.stream && this.streams && this.streams[p.stream] && this.streams[p.stream].box);
      const entry = this.planId
          ? [this.planId, (this.plans || {})[this.planId]]
          : Object.entries(this.plans || {}).find(([, p]) => onBoxStream(p));
      return entry && entry[1] ? {...entry[1], id: entry[0]} : null;
    },
    streamIds() {
      return Object.keys(this.streams || {}).sort();
    },
    // per-stream status under the CURRENT edits: visible streams plus any
    // stream the rules mention, with excluded/included and surcharge chips
    streamSummary() {
      if (!this.planCopy) return [];
      const lower = v => `${v}`.toLowerCase();
      const filters = (this.planCopy.box_filters || []).filter(f => f && f.attribute === 'stream');
      const surcharges = this.planCopy.box_surcharges || [];
      const isExcluded = id => filters.some(f =>
          (Array.isArray(f.exclude) && f.exclude.map(lower).includes(lower(id)))
          || (Array.isArray(f.include) && !f.include.map(lower).includes(lower(id))));
      const mentioned = [
        ...filters.flatMap(f => [...(f.exclude || []), ...(f.include || [])]),
        ...surcharges.flatMap(r => r.streams || []),
      ];
      const ids = [...new Set([
        ...this.streamIds.filter(id => this.streams[id] && this.streams[id].visible),
        ...mentioned,
      ])].sort();
      return ids
          .map(id => {
            const excluded = isExcluded(id);
            return {
              id,
              hidden: !(this.streams[id] && this.streams[id].visible),
              excluded,
              surcharges: excluded ? [] : surcharges
                  .filter(r => Array.isArray(r.streams) && r.streams.map(lower).includes(lower(id)))
                  .map(r => `+${this.formatCurrency(r.amount)}`
                      + (Array.isArray(r.tods) && r.tods.length ? ` (${r.tods.join(', ')})` : '')),
            };
          })
          .sort((a, b) => (a.excluded - b.excluded) || a.id.localeCompare(b.id));
    },
    // which meal types (tods / CC categories) the CURRENT filters let into
    // the box — every known tod plus any value the rules mention
    todSummary() {
      if (!this.planCopy) return {included: [], excluded: []};
      const lower = v => `${v}`.toLowerCase();
      const todFilters = (this.planCopy.box_filters || []).filter(f => f && f.attribute === 'tod');
      const mentioned = todFilters
          .flatMap(f => [...(f.include || []), ...(f.exclude || [])])
          .map(lower);
      const tods = [...new Set([...this.knownTods, ...mentioned])].sort();
      const isIncluded = tod => !todFilters.some(f =>
          (Array.isArray(f.exclude) && f.exclude.map(lower).includes(lower(tod)))
          || (Array.isArray(f.include) && !f.include.map(lower).includes(lower(tod))));
      return {
        included: tods.filter(isIncluded),
        excluded: tods.filter(t => !isIncluded(t)),
      };
    },
    // the base_price window under the current filters, in dollars
    priceWindow() {
      if (!this.planCopy) return '';
      const parts = [];
      for (const f of this.planCopy.box_filters || []) {
        if (!f || f.attribute !== 'base_price') continue;
        if (f.min != null && f.min !== '') parts.push(`at least ${this.formatCurrency(f.min)}`);
        if (f.max != null && f.max !== '') parts.push(`at most ${this.formatCurrency(f.max)}`);
      }
      return parts.join(' and ');
    },
    changed() {
      if (!this.planCopy || !this.boxPlan) return false;
      return diff(this.normalise(this.boxPlan), this.normalise(this.planCopy)).length > 0;
    },
  },
  methods: {
    ...mapActions(['fetchPlans', 'fetchStreams', 'updatePlan']),
    formatCurrency(cents) {
      return isNaN(Number(cents)) ? '?' : formatCurrency(Number(cents));
    },
    resetCopy() {
      if (!this.boxPlan) {
        this.planCopy = null;
        return;
      }
      const copy = deepCopy(this.boxPlan);
      copy.box_sizes = copy.box_sizes || [];
      copy.box_filters = copy.box_filters || [];
      copy.box_surcharges = copy.box_surcharges || [];
      // every editable field must EXIST before the copy turns reactive —
      // Vue 2 can't track v-model writes to keys the data never had (the
      // seed only sets free_shipping on the free tiers, so the other
      // rows' checkboxes were dead)
      copy.box_sizes.forEach(s => {
        s.free_shipping = !!s.free_shipping;
      });
      copy.breakfast_fruitcup = !!copy.breakfast_fruitcup;
      if (copy.box_max_size === undefined) copy.box_max_size = null;
      for (const key of BOX_CC_RULE_KEYS) {
        if (copy[key] === undefined) copy[key] = null;
      }
      copy.hidden_preferences = Array.isArray(copy.hidden_preferences) ? copy.hidden_preferences : [];
      copy.box_filters.forEach(f => {
        if (!f.include && !f.exclude) {
          if (f.min === undefined) f.min = null;
          if (f.max === undefined) f.max = null;
        }
      });
      // the rule arrays must exist before the copy turns reactive (same
      // Vue 2 rule as free_shipping above); normalise() strips empties on
      // save so an untouched section never writes {exclude: [], ...}
      for (const key of ['box_autopick_menu_categories', 'box_swap_menu_categories']) {
        const rules = copy[key] || {};
        copy[key] = {
          exclude: Array.isArray(rules.exclude) ? rules.exclude : [],
          include: Array.isArray(rules.include) ? rules.include : [],
        };
      }
      this.planCopy = copy;
    },
    // text inputs turn numbers into strings — compare (and save) numbers
    normalise(plan) {
      const p = deepCopy(plan);
      p.box_sizes = p.box_sizes || [];
      p.box_filters = p.box_filters || [];
      p.box_surcharges = p.box_surcharges || [];
      const num = v => (v === '' || v === null || v === undefined) ? null : Number(v);
      if (p.box_base_price != null) p.box_base_price = num(p.box_base_price);
      p.box_max_size = num(p.box_max_size);
      if (p.box_max_size === null) delete p.box_max_size;
      // empty = off: the key drops out of the plan
      for (const key of BOX_CC_RULE_KEYS) {
        p[key] = num(p[key]);
        if (p[key] === null || isNaN(p[key])) delete p[key];
      }
      p.breakfast_fruitcup = !!p.breakfast_fruitcup;
      p.box_sizes.forEach(s => {
        s.size = num(s.size);
        s.price = num(s.price);
        s.free_shipping = !!s.free_shipping;
      });
      p.box_surcharges.forEach(r => {
        r.amount = num(r.amount);
      });
      p.box_filters.forEach(f => {
        if (f.min !== undefined) f.min = num(f.min);
        if (f.max !== undefined) f.max = num(f.max);
        if (f.min === null) delete f.min;
        if (f.max === null) delete f.max;
      });
      // an empty hidden-preferences list drops out too
      if (!Array.isArray(p.hidden_preferences) || !p.hidden_preferences.length) {
        delete p.hidden_preferences;
      }
      // empty menu-category rules drop out entirely (the backend replaces
      // the plan node wholesale, so an absent key removes it from the RTDB)
      for (const key of ['box_autopick_menu_categories', 'box_swap_menu_categories']) {
        const raw = p[key] || {};
        const rules = {};
        if (Array.isArray(raw.exclude) && raw.exclude.length) rules.exclude = raw.exclude;
        if (Array.isArray(raw.include) && raw.include.length) rules.include = raw.include;
        if (Object.keys(rules).length) p[key] = rules;
        else delete p[key];
      }
      return p;
    },
    // the report for the preview customer, ranked with the chef's choice
    // settings as the form holds them (null = off), saved or not
    async openPreview() {
      const customer = (this.preview.customer || '').trim();
      if (this.preview.loading) return;
      try {
        localStorage.setItem(PREVIEW_CUSTOMER_KEY, customer);
      } catch (e) {
        // private mode: not remembered
      }
      const plan = this.normalise(this.planCopy);
      const cc_rules = {};
      BOX_CC_RULE_KEYS.forEach(key => {
        cc_rules[key] = plan[key] === undefined ? null : plan[key];
      });
      this.preview.loading = true;
      try {
        const {data} = await api.post('v2/box-ranking-report', {
          customer, plan_id: plan.id, cc_rules, weeks: this.preview.weeks,
        }, {responseType: 'text'});
        this.preview.html = data;
        this.preview.shownFor = customer;
        this.preview.open = true;
      } catch (e) {
        const error = e.response && e.response.data;
        const message = (error && error.error) || (typeof error === 'string' && error) || e.message;
        this.snackbarText = `preview failed: ${message}`;
        this.snackbar = true;
      } finally {
        this.preview.loading = false;
      }
    },
    async fetchMenuCategories() {
      this.menuCategoriesLoading = true;
      try {
        // the LIVE list (what the website matches against), not the draft
        const {data} = await api.get('v2/menu_categories');
        this.menuCategoryNames = (Array.isArray(data) ? data : [])
            .map(c => c && c.name)
            .filter(Boolean);
      } catch (e) {
        console.error('failed to load menu categories', e);
      } finally {
        this.menuCategoriesLoading = false;
      }
    },
    filterType(f) {
      if (f.include) return 'include';
      if (f.exclude) return 'exclude';
      return 'range';
    },
    setFilterType(f, type) {
      this.$delete(f, 'include');
      this.$delete(f, 'exclude');
      this.$delete(f, 'min');
      this.$delete(f, 'max');
      if (type === 'include') this.$set(f, 'include', []);
      else if (type === 'exclude') this.$set(f, 'exclude', []);
      else {
        this.$set(f, 'min', null);
        this.$set(f, 'max', null);
      }
    },
    valuesFor(attribute) {
      if (attribute === 'stream') return this.streamIds;
      if (attribute === 'tod') return this.knownTods;
      return [];
    },
    openSizeDialog(index) {
      const row = index === null ? {size: null, price: null, free_shipping: false} : this.planCopy.box_sizes[index];
      this.sizeDialog = {
        open: true, index, original: row.size,
        size: row.size, price: row.price, free_shipping: !!row.free_shipping, error: '',
      };
    },
    applySize() {
      const d = this.sizeDialog;
      const size = Number(d.size);
      const price = Number(d.price);
      if (!(Number.isInteger(size) && size > 0) || !(Number.isInteger(price) && price > 0)) {
        d.error = 'size and price (cents) must be whole numbers above 0';
        return;
      }
      if (this.planCopy.box_sizes.some((s, i) => i !== d.index && Number(s.size) === size)) {
        d.error = `the plan already has a box of ${size}`;
        return;
      }
      const row = {size, price, free_shipping: !!d.free_shipping};
      if (d.index === null) this.planCopy.box_sizes.push(row);
      else this.planCopy.box_sizes.splice(d.index, 1, {...this.planCopy.box_sizes[d.index], ...row});
      // the ladder reads smallest first
      this.planCopy.box_sizes.sort((a, b) => Number(a.size) - Number(b.size));
      d.open = false;
    },
    deleteSize() {
      this.planCopy.box_sizes.splice(this.sizeDialog.index, 1);
      this.sizeDialog.open = false;
    },
    addSurcharge() {
      this.planCopy.box_surcharges.push({streams: [], tods: ['entree'], amount: 250});
    },
    addFilter() {
      this.planCopy.box_filters.push({attribute: 'tod', include: []});
    },
    async save() {
      const update = this.normalise(this.planCopy);
      if (!(update.name || '').trim()) {
        this.snackbarText = 'the plan needs a name';
        this.snackbar = true;
        return;
      }
      const badSize = update.box_sizes.find(s => !(s.size > 0) || !(s.price > 0));
      if (badSize) {
        this.snackbarText = 'every box size needs a positive size and price (cents)';
        this.snackbar = true;
        return;
      }
      const largest = Math.max(0, ...update.box_sizes.map(s => s.size));
      if (update.box_max_size != null && !(update.box_max_size >= largest)) {
        this.snackbarText = `the maximum box size must be at least the largest tier (${largest})`;
        this.snackbar = true;
        return;
      }
      const badCount = ['box_cc_lead_limit', 'box_cc_per_delivery']
          .find(key => update[key] != null && !(Number.isInteger(update[key]) && update[key] >= 0));
      const badPoints = ['box_cc_rotation', 'box_cc_featured_bonus'].find(key => update[key] != null && !(update[key] >= 0));
      if (badCount || badPoints) {
        this.snackbarText = badCount
            ? `${badCount} must be a whole number, 0 or more`
            : `${badPoints} must be 0 or more`;
        this.snackbar = true;
        return;
      }
      this.saving = true;
      try {
        // full-object save: the backend replaces the plan node wholesale,
        // so the copy must carry every plan field, not just the box ones
        await this.updatePlan({id: update.id, update});
        this.snackbarText = 'saved';
      } catch (e) {
        console.error('failed to save box plan', e);
        this.snackbarText = `save failed: ${e.message || e}`;
      } finally {
        this.saving = false;
        this.snackbar = true;
      }
    },
  },
}
</script>

<style scoped>
.box-plan-tabs {
  height: 70vh;
}

.box-ranking-preview {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

/* a v-toolbar grows (flex 1 1 auto) in a flex column: keep it its own height */
.box-ranking-preview .v-toolbar {
  flex: 0 0 auto;
}

.box-ranking-preview iframe {
  flex: 1;
  width: 100%;
  border: 0;
}
</style>